/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useCallback } from 'react';
import {
  getWorktrees,
  getWorktreeStatus,
  getCommitHistory,
  deleteWorktree,
  type WorktreeInfo as WorktreeInfoType,
} from '../../lib/api';
import type { WorktreeInfo, WorktreeStatus, CommitHistory } from '../../components/changes';
import type { SourceControlProvider } from '../../components/changes';

interface ActiveProduct {
  repositoryUrl?: string | null;
  repoPath?: string | null;
  title?: string;
}

interface ToastFn {
  (opts: { title: string; description: string; variant?: 'destructive' }): void;
}

export function useWorktreeManagement(
  activeProduct: ActiveProduct | null,
  sourceControlProvider: SourceControlProvider,
  toast: ToastFn
) {
  const [worktrees, setWorktrees] = useState<WorktreeInfo[]>([]);
  const [selectedWorktree, setSelectedWorktree] = useState<WorktreeInfo | null>(null);
  const [status, setStatus] = useState<WorktreeStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [commitHistory, setCommitHistory] = useState<CommitHistory | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  // Delete worktree state
  const [showDeleteWorktreeDialog, setShowDeleteWorktreeDialog] = useState(false);
  const [worktreeToDelete, setWorktreeToDelete] = useState<WorktreeInfo | null>(null);
  const [deleteWorktreeStatus, setDeleteWorktreeStatus] = useState<{
    hasUnmergedChanges: boolean;
    uncommittedFilesCount: number;
    unpushedCommitsCount: number;
  }>({ hasUnmergedChanges: false, uncommittedFilesCount: 0, unpushedCommitsCount: 0 });
  const [deletingWorktree, setDeletingWorktree] = useState(false);

  const loadWorktrees = useCallback(async () => {
    if (!activeProduct?.repositoryUrl) return;

    setLoading(true);
    try {
      const result = await getWorktrees(activeProduct.repositoryUrl, sourceControlProvider);
      if (!result.success || !result.worktrees) {
        throw new Error(result.error || 'Failed to load worktrees');
      }
      setWorktrees(result.worktrees);

      if (!selectedWorktree && result.worktrees.length > 0) {
        const mainWorktree = result.worktrees.find((w: WorktreeInfoType) => w.path.endsWith('/velocity'));
        setSelectedWorktree(mainWorktree || result.worktrees[0]);
      }
    } catch (error: any) {
      console.error('Error loading worktrees:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load worktrees',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [activeProduct, selectedWorktree, sourceControlProvider, toast]);

  const loadStatus = useCallback(async () => {
    if (!activeProduct?.repositoryUrl || !selectedWorktree) return;

    setRefreshing(true);
    try {
      const result = await getWorktreeStatus(
        activeProduct.repositoryUrl,
        selectedWorktree.path,
        activeProduct.repoPath || undefined
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to load status');
      }
      setStatus(result.status || null);
    } catch (error: any) {
      console.error('Error loading status:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load status',
        variant: 'destructive'
      });
    } finally {
      setRefreshing(false);
    }
  }, [activeProduct, selectedWorktree, toast]);

  const loadCommitHistory = useCallback(async () => {
    if (!activeProduct?.repositoryUrl || !selectedWorktree) return;

    setLoadingHistory(true);
    try {
      const result = await getCommitHistory(
        activeProduct.repositoryUrl,
        selectedWorktree.path,
        50
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to load commit history');
      }
      setCommitHistory(result.commits ? {
        branch: result.branch || '',
        hasRemote: result.hasRemote || false,
        remoteCommit: result.remoteCommit || null,
        commits: result.commits,
        unpushedCount: result.unpushedCount || 0
      } : null);
    } catch (error: any) {
      console.error('Error loading commit history:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load commit history',
        variant: 'destructive'
      });
    } finally {
      setLoadingHistory(false);
    }
  }, [activeProduct, selectedWorktree, toast]);

  const handleDeleteWorktreeRequest = useCallback(async (worktree: WorktreeInfo) => {
    if (!activeProduct?.repositoryUrl) return;
    setWorktreeToDelete(worktree);

    try {
      const statusResult = await getWorktreeStatus(
        activeProduct.repositoryUrl,
        worktree.path,
        activeProduct.repoPath || undefined
      );
      const historyResult = await getCommitHistory(
        activeProduct.repositoryUrl,
        worktree.path,
        50
      );
      const uncommittedFilesCount = statusResult.status?.files?.length || 0;
      const unpushedCommitsCount = historyResult.unpushedCount || 0;
      const hasUnmergedChanges = uncommittedFilesCount > 0 || unpushedCommitsCount > 0;
      setDeleteWorktreeStatus({ hasUnmergedChanges, uncommittedFilesCount, unpushedCommitsCount });
    } catch (error) {
      console.error('[Changes] Error checking worktree status for delete:', error);
      setDeleteWorktreeStatus({ hasUnmergedChanges: true, uncommittedFilesCount: 0, unpushedCommitsCount: 0 });
    }

    setShowDeleteWorktreeDialog(true);
  }, [activeProduct]);

  const handleDeleteWorktreeConfirm = useCallback(async () => {
    if (!activeProduct?.repositoryUrl || !worktreeToDelete) return;

    setDeletingWorktree(true);
    try {
      const result = await deleteWorktree(
        activeProduct.repositoryUrl,
        worktreeToDelete.path,
        true,
        sourceControlProvider
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete worktree');
      }
      toast({
        title: 'Worktree Deleted',
        description: `Successfully deleted worktree: ${worktreeToDelete.path.split('/').pop()}`
      });

      setShowDeleteWorktreeDialog(false);
      setWorktreeToDelete(null);

      if (selectedWorktree?.path === worktreeToDelete.path) {
        setSelectedWorktree(null);
        setStatus(null);
        setCommitHistory(null);
      }

      await loadWorktrees();
    } catch (error: any) {
      console.error('[Changes] Error deleting worktree:', error);
      toast({
        title: 'Delete Failed',
        description: error.message || 'Failed to delete worktree',
        variant: 'destructive'
      });
    } finally {
      setDeletingWorktree(false);
    }
  }, [activeProduct, worktreeToDelete, sourceControlProvider, selectedWorktree, toast, loadWorktrees]);

  return {
    worktrees,
    selectedWorktree,
    setSelectedWorktree,
    status,
    loading,
    refreshing,
    loadWorktrees,
    loadStatus,

    commitHistory,
    loadingHistory,
    historyExpanded,
    setHistoryExpanded,
    loadCommitHistory,

    showDeleteWorktreeDialog,
    setShowDeleteWorktreeDialog,
    worktreeToDelete,
    deleteWorktreeStatus,
    deletingWorktree,
    handleDeleteWorktreeRequest,
    handleDeleteWorktreeConfirm,
  };
}
